import {Search, Filter, X, RotateCcw} from 'lucide-react'
import type { CargoType, TransportMode } from '@/lib/freight'

interface LeadFiltersProps {
  search: string
  onSearchChange: (v: string) => void
  modeFilter: TransportMode | 'ALL'
  onModeFilterChange: (v: TransportMode | 'ALL') => void
  cargoTypeFilter: CargoType | 'ALL'
  onCargoTypeFilterChange: (v: CargoType | 'ALL') => void
  dateFrom: string
  onDateFromChange: (v: string) => void
  dateTo: string
  onDateToChange: (v: string) => void
  minRate: string
  onMinRateChange: (v: string) => void
  maxRate: string
  onMaxRateChange: (v: string) => void
  hasActiveFilters: boolean
  onReset: () => void
}

const MODE_FILTERS: Array<{ label: string; value: TransportMode | 'ALL' }> = [
  { label: 'All Modes', value: 'ALL' },
  { label: 'LTL', value: 'LTL' },
  { label: 'FTL', value: 'FTL' },
  { label: 'Ocean', value: 'OCEAN' },
  { label: 'Air', value: 'AIR' },
]

const CARGO_TYPE_FILTERS: Array<{ label: string; value: CargoType | 'ALL' }> = [
  { label: 'All Cargo Types', value: 'ALL' },
  { label: 'Standard', value: 'Standard' },
  { label: 'Hazmat', value: 'Hazmat' },
  { label: 'Refrigerated', value: 'Refrigerated' },
]

function LeadFilters({
  search, onSearchChange,
  modeFilter, onModeFilterChange,
  cargoTypeFilter, onCargoTypeFilterChange,
  dateFrom, onDateFromChange,
  dateTo, onDateToChange,
  minRate, onMinRateChange,
  maxRate, onMaxRateChange,
  hasActiveFilters, onReset,
}: LeadFiltersProps) {
  return (
    <div className="p-4 border-b border-slate-200 space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search name, email, phone, lane..."
            className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-9 py-2 text-sm text-[#0F172A] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#10B981] focus:border-transparent transition"
          />
          {search ? (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          ) : null}
        </div>

        <div className="flex items-center gap-2 overflow-x-auto">
          {MODE_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => onModeFilterChange(f.value)}
              className={`shrink-0 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                modeFilter === f.value
                  ? 'bg-[#1E293B] text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400 shrink-0">
          <Filter className="w-3.5 h-3.5" />
          Filters
        </div>

        <select
          value={cargoTypeFilter}
          onChange={(e) => onCargoTypeFilterChange(e.target.value as CargoType | 'ALL')}
          className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#10B981] focus:border-transparent transition"
        >
          {CARGO_TYPE_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>

        <div className="flex items-center gap-1.5">
          <label className="text-xs text-slate-400">Ready from</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => onDateFromChange(e.target.value)}
            className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#10B981] focus:border-transparent transition"
          />
        </div>

        <div className="flex items-center gap-1.5">
          <label className="text-xs text-slate-400">to</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => onDateToChange(e.target.value)}
            className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#10B981] focus:border-transparent transition"
          />
        </div>

        <div className="flex items-center gap-1.5">
          <label className="text-xs text-slate-400">Rate $</label>
          <input
            type="number"
            min="0"
            value={minRate}
            onChange={(e) => onMinRateChange(e.target.value)}
            placeholder="Min"
            className="w-20 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-[#0F172A] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#10B981] focus:border-transparent transition"
          />
          <span className="text-slate-300">–</span>
          <input
            type="number"
            min="0"
            value={maxRate}
            onChange={(e) => onMaxRateChange(e.target.value)}
            placeholder="Max"
            className="w-20 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-[#0F172A] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#10B981] focus:border-transparent transition"
          />
        </div>

        {hasActiveFilters ? (
          <button
            onClick={onReset}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset filters
          </button>
        ) : null}
      </div>
    </div>
  )
}

export default LeadFilters
